import React, { useState } from 'react';
import { AopLink, Card, InternalCard, Modal, Pill, StatusPill, WarnRow } from '../ui';
import { ALLOCATION_MODES, EXPERIMENT_TYPES, experimentDiagnostics, liveExperiment } from '../copData';
import { useCop } from '../CopApp';

function AllocationChart({ series, variants }) {
	const W = 720;
	const H = 200;
	const PAD = 26;
	const PAD_L = 40;
	const maxY = 70;
	const x = (i) => PAD_L + (i / (series.length - 1)) * (W - PAD_L - PAD);
	const y = (v) => H - PAD - (v / maxY) * (H - PAD * 2);
	const line = (key) => series.map((d, i) => `${x(i)},${y(d[key])}`).join(' ');
	const keys = [
		{ key: 'a', color: variants[0].color },
		{ key: 'b', color: variants[1].color },
		{ key: 'c', color: variants[2].color },
	];
	return (
		<svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
			{[0, 20, 40, 60].map((g) => (
				<g key={g}>
					<line x1={PAD_L} x2={W - PAD} y1={y(g)} y2={y(g)} stroke="rgba(148,163,216,0.08)" />
					<text x={PAD_L - 7} y={y(g) + 3} textAnchor="end" fontSize="8.5" fill="#5d6488" fontFamily="IBM Plex Mono, monospace">
						{g}%
					</text>
				</g>
			))}
			{keys.map(({ key, color }) => (
				<polyline key={key} points={line(key)} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
			))}
			{keys.map(({ key, color }) => {
				const last = series[series.length - 1];
				return <circle key={key} cx={x(series.length - 1)} cy={y(last[key])} r="3.4" fill={color} />;
			})}
			{series.map((d, i) =>
				i % 2 === 0 ? (
					<text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="8.5" fill="#5d6488" fontFamily="IBM Plex Mono, monospace">
						d{d.day}
					</text>
				) : null
			)}
		</svg>
	);
}

function ConfidenceRanges({ variants }) {
	const min = 2;
	const max = 8;
	const pct = (v) => `${((v - min) / (max - min)) * 100}%`;
	return (
		<>
			{variants.map((v) => (
				<div className="cop-ci-row" key={v.variant_id}>
					<span style={{ color: v.color, fontWeight: 600 }}>{v.variant_name}</span>
					<div className="cop-ci-track">
						<span
							className="cop-ci-band"
							style={{ left: pct(v.ci[0]), width: `calc(${pct(v.ci[1])} - ${pct(v.ci[0])})`, background: v.color, opacity: 0.4 }}
						/>
						<span className="cop-ci-mark" style={{ left: pct(v.conversion_rate) }} />
					</div>
					<span className="cop-mono" style={{ color: 'var(--text-sec)' }}>
						{v.ci[0]}% – {v.ci[1]}%
					</span>
				</div>
			))}
		</>
	);
}

const EMPTY_EXPERIMENT = {
	experiment_name: '',
	experiment_type: 'split_test',
	status: 'draft',
	target_segment_id: '',
	allocation_mode: 'approval',
	allocation_period_days: 7,
	experiment_length_days: 21,
	confidence_pct: 95,
	variants: [
		{ variant_name: 'Control', allocation: 50 },
		{ variant_name: 'Variant B', allocation: 50 },
	],
};

function ExperimentForm({ form, setForm, segments, error }) {
	const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
	const setVariant = (i, key, value) =>
		setForm((f) => ({ ...f, variants: f.variants.map((v, idx) => (idx === i ? { ...v, [key]: value } : v)) }));
	const addVariant = () =>
		setForm((f) => ({
			...f,
			variants: [...f.variants, { variant_name: `Variant ${String.fromCharCode(65 + f.variants.length)}`, allocation: 0 }],
		}));
	const removeVariant = (i) => setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));
	const total = form.variants.reduce((sum, v) => sum + (Number(v.allocation) || 0), 0);

	return (
		<div className="cop-form-grid">
			<label className="cop-form-field cop-form-span">
				<span>Experiment name *</span>
				<input className="cop-input" value={form.experiment_name} onChange={set('experiment_name')} placeholder="e.g. Win-Back Offer Test" />
			</label>
			<label className="cop-form-field">
				<span>Type</span>
				<select className="cop-select" value={form.experiment_type} onChange={set('experiment_type')}>
					{EXPERIMENT_TYPES.map((t) => (
						<option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
					))}
				</select>
			</label>
			<label className="cop-form-field">
				<span>Status</span>
				<select className="cop-select" value={form.status} onChange={set('status')}>
					{['draft', 'active', 'paused', 'concluded', 'archived'].map((s) => (
						<option key={s} value={s}>{s}</option>
					))}
				</select>
			</label>
			<label className="cop-form-field cop-form-span">
				<span>Target segment</span>
				<select className="cop-select" value={form.target_segment_id} onChange={set('target_segment_id')}>
					<option value="">— none —</option>
					{segments.filter((s) => s.is_targetable).map((s) => (
						<option key={s.segment_id} value={s.segment_id}>
							{s.segment_name}{s.source === 'custom' ? ' (custom)' : ''}
						</option>
					))}
				</select>
			</label>
			<label className={`cop-form-field${form.allocation_mode === 'periodic' ? '' : ' cop-form-span'}`}>
				<span>Traffic allocation changes</span>
				<select className="cop-select" value={form.allocation_mode} onChange={set('allocation_mode')}>
					{ALLOCATION_MODES.map((m) => (
						<option key={m.value} value={m.value}>{m.label}</option>
					))}
				</select>
				<em className="cop-field-hint">{ALLOCATION_MODES.find((m) => m.value === form.allocation_mode)?.hint}</em>
			</label>
			{form.allocation_mode === 'periodic' && (
				<label className="cop-form-field">
					<span>Reallocate every</span>
					<input className="cop-input" type="number" min="1" max="90" value={form.allocation_period_days} onChange={set('allocation_period_days')} />
					<em className="cop-field-hint">days between automatic reallocations</em>
				</label>
			)}
			<label className="cop-form-field">
				<span>Experiment length</span>
				<input className="cop-input" type="number" min="1" max="365" value={form.experiment_length_days} onChange={set('experiment_length_days')} />
				<em className="cop-field-hint">days before a winner is selected</em>
			</label>
			<label className="cop-form-field">
				<span>Confidence to declare</span>
				<input className="cop-input" type="number" min="50" max="99" value={form.confidence_pct} onChange={set('confidence_pct')} />
				<em className="cop-field-hint">% best-probability that ends the test early</em>
			</label>

			<div className="cop-form-span">
				<div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0 6px' }}>
					<span style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 1 }}>Variants</span>
					<span className="cop-mono" style={{ fontSize: 11, color: Math.abs(total - 100) > 0.5 ? 'var(--red)' : 'var(--green)' }}>
						total {total}%
					</span>
					<button type="button" className="cop-btn small" style={{ marginLeft: 'auto' }} onClick={addVariant} disabled={form.variants.length >= 8}>
						+ Variant
					</button>
				</div>
				{form.variants.map((v, i) => (
					<div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
						<input
							className="cop-input"
							style={{ flex: 1 }}
							value={v.variant_name}
							onChange={(e) => setVariant(i, 'variant_name', e.target.value)}
							placeholder={`Variant ${i + 1} name`}
						/>
						<input
							className="cop-input"
							type="number"
							min="0"
							max="100"
							style={{ width: 90 }}
							value={v.allocation}
							onChange={(e) => setVariant(i, 'allocation', e.target.value)}
						/>
						<button type="button" className="cop-btn small danger" onClick={() => removeVariant(i)} disabled={form.variants.length <= 2}>
							✕
						</button>
					</div>
				))}
			</div>
			{error && <div className="cop-form-error cop-form-span">{error}</div>}
		</div>
	);
}

export default function ExperimentLab() {
	const exp = liveExperiment;
	const { showToast, recordAudit, navigate, isInternal, segments, experiments, createExperiment, updateExperiment, deleteExperiment, reportDateRangeLabel, defaultAllocationMode, defaultExperimentLength, defaultConfidencePct } = useCop();
	const [winnerModal, setWinnerModal] = useState(false);
	const [deepDive, setDeepDive] = useState(true);
	const [modal, setModal] = useState(null); // { mode: 'create' | 'edit', id? }
	const [form, setForm] = useState(EMPTY_EXPERIMENT);
	const [error, setError] = useState('');
	const [saving, setSaving] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(null);

	const declareWinner = () => {
		setWinnerModal(false);
		recordAudit({
			actor_type: 'ai_agent',
			actor: 'experiment_bot',
			action: 'submitted Declare Test Winner for approval',
			entity: exp.experiment_name,
			entity_type: 'experiment',
			source: 'worker',
		});
		showToast('Winner declaration submitted to Approval Queue');
		navigate('approvals');
	};

	const segmentName = (id) => segments.find((s) => s.segment_id === id)?.segment_name;

	const openCreate = () => {
		// New experiments inherit the account-wide defaults set in Settings.
		setForm({
			...EMPTY_EXPERIMENT,
			allocation_mode: defaultAllocationMode,
			experiment_length_days: defaultExperimentLength,
			confidence_pct: defaultConfidencePct,
		});
		setError('');
		setModal({ mode: 'create' });
	};

	const openEdit = (experiment) => {
		setForm({
			experiment_name: experiment.experiment_name,
			experiment_type: experiment.experiment_type,
			status: experiment.status,
			target_segment_id: experiment.target_segment_id || '',
			allocation_mode: experiment.allocation_mode || defaultAllocationMode,
			allocation_period_days: experiment.allocation_period_days || 7,
			experiment_length_days: experiment.experiment_length_days || 21,
			confidence_pct: experiment.confidence_pct || 95,
			variants: experiment.variants.length ? experiment.variants.map((v) => ({ ...v })) : EMPTY_EXPERIMENT.variants,
		});
		setError('');
		setModal({ mode: 'edit', id: experiment.experiment_id });
	};

	const submit = () => {
		if (!form.experiment_name.trim()) {
			setError('Experiment name is required.');
			return;
		}
		if (form.variants.length < 2) {
			setError('At least two variants are required.');
			return;
		}
		if (form.variants.some((v) => !v.variant_name.trim())) {
			setError('Every variant needs a name.');
			return;
		}
		const total = form.variants.reduce((sum, v) => sum + (Number(v.allocation) || 0), 0);
		if (Math.abs(total - 100) > 0.5) {
			setError(`Variant allocations must total 100% (currently ${total}%).`);
			return;
		}
		if (!(Number(form.experiment_length_days) >= 1)) {
			setError('Experiment length must be at least 1 day.');
			return;
		}
		const conf = Number(form.confidence_pct);
		if (!(conf >= 50 && conf <= 99)) {
			setError('Confidence to declare must be between 50% and 99%.');
			return;
		}
		if (form.allocation_mode === 'periodic' && !(Number(form.allocation_period_days) >= 1)) {
			setError('Reallocation interval must be at least 1 day.');
			return;
		}
		setSaving(true);
		// Brief async simulation so the save state is visible (the seam to /api/v1/cop/experiments).
		setTimeout(() => {
			const payload = {
				experiment_name: form.experiment_name.trim(),
				experiment_type: form.experiment_type,
				status: form.status,
				target_segment_id: form.target_segment_id || null,
				allocation_mode: form.allocation_mode,
				allocation_period_days: Number(form.allocation_period_days) || 7,
				experiment_length_days: Number(form.experiment_length_days) || 21,
				confidence_pct: Number(form.confidence_pct) || 95,
				variants: form.variants.map((v) => ({ variant_name: v.variant_name.trim(), allocation: Number(v.allocation) || 0 })),
			};
			if (modal.mode === 'create') createExperiment(payload);
			else updateExperiment(modal.id, payload);
			setSaving(false);
			setModal(null);
		}, 350);
	};

	const doDelete = () => {
		deleteExperiment(confirmDelete.experiment_id);
		setConfirmDelete(null);
	};

	return (
		<main className="cop-page">
			<div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
				<div style={{ flex: 1, minWidth: 280 }}>
					<h1 className="cop-page-title">
						{exp.experiment_name} <Pill tone="green">live</Pill>
					</h1>
					<div className="cop-page-sub">
						Experiment Lab · {reportDateRangeLabel} · {exp.variants.length} variants · powered by {exp.method} · day {exp.day} of {exp.of_days}
					</div>
				</div>
				<button className="cop-btn" onClick={() => showToast('Experiment extended 7 days')}>Extend Experiment</button>
				<button className="cop-btn primary" onClick={() => setWinnerModal(true)}>Instant Winner</button>
			</div>

			<Card title="Business Outcome" icon="◎" right={exp.business_question}>
				<div className="cop-outcome-banner" style={{ border: 0, padding: 0, background: 'transparent' }}>
					<div className="cop-outcome-cell">
						<div className="l">Winning variant</div>
						<div className="v tone-pink">{exp.outcome.winner}</div>
					</div>
					<div className="cop-outcome-cell">
						<div className="l">Revenue / session</div>
						<div className="v tone-green">{exp.outcome.rps_delta}</div>
					</div>
					<div className="cop-outcome-cell">
						<div className="l">Projected annual</div>
						<div className="v tone-green">{exp.outcome.projected_annual}</div>
					</div>
					<div className="cop-outcome-cell">
						<div className="l">Best probability</div>
						<div className="v tone-amber">{Math.round(exp.outcome.confidence * 100)}%</div>
					</div>
				</div>
			</Card>

			<div className="cop-variant-grid">
				{exp.variants.map((v) => (
					<div className={`cop-variant-card${v.winner ? ' winner' : ''}`} key={v.variant_id}>
						<div className="cop-variant-head">
							<span className="cop-legend-dot" style={{ color: v.color, background: v.color }} />
							{v.variant_name}
							<span style={{ fontFamily: 'var(--font-ui)', fontWeight: 500, fontSize: 11, color: 'var(--text-dim)' }}>
								{v.label}
							</span>
							{v.winner && <span style={{ marginLeft: 'auto' }}><Pill tone="pink">leading</Pill></span>}
						</div>
						<div className="cop-variant-metrics">
							<div className="m">
								<div className="v">{v.conversion_rate}%</div>
								<div className="l">Conv rate</div>
							</div>
							<div className="m">
								<div className="v">${v.rpv.toFixed(2)}</div>
								<div className="l">RPV</div>
							</div>
							<div className="m">
								<div className="v">{Math.round(v.best_probability * 100)}%</div>
								<div className="l">Best prob</div>
							</div>
						</div>
						<div className="cop-alloc-track">
							<div className="cop-alloc-fill" style={{ width: `${v.allocation}%`, background: v.color }} />
						</div>
						<div style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
							{v.allocation}% of traffic
						</div>
					</div>
				))}
			</div>

			<Card
				title="Traffic Allocation Shift"
				icon="↝"
				right={
					<button className="cop-link" onClick={() => setDeepDive((d) => !d)}>
						{deepDive ? 'Hide' : 'Show'} statistical deep dive
					</button>
				}
			>
				<AllocationChart series={exp.allocation_series} variants={exp.variants} />
				<div className="cop-scatter-legend">
					{exp.variants.map((v) => (
						<span key={v.variant_id}>
							<span className="cop-legend-dot" style={{ color: v.color, background: v.color }} />
							{v.variant_name} · {v.label}
						</span>
					))}
				</div>
			</Card>

			{deepDive && (
				<Card title="Bayesian Confidence Range" icon="∿" right="posterior conversion-rate intervals, 95%">
					<ConfidenceRanges variants={exp.variants} />
					<div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-dim)' }}>
						Decision rule: declare when best-probability ≥ 95% or at day {exp.of_days}, whichever comes first.
						Winner declaration is a governed action — it routes through the Approval Queue.
					</div>
				</Card>
			)}

			{isInternal && (
				<InternalCard title="Engine Diagnostics" icon="⚗" right={experimentDiagnostics.engine}>
					{experimentDiagnostics.warnings.map((w, i) => (
						<WarnRow key={i} level={w.level}>{w.text}</WarnRow>
					))}
					<div className="cop-aop-links">
						<AopLink view="workflow-runs">experiment_bot monitor</AopLink>
						<AopLink view="pending-actions">winner declarations</AopLink>
					</div>
				</InternalCard>
			)}

			<Card
				title="Experiments"
				icon="▥"
				right={<button className="cop-btn small primary" onClick={openCreate}>+ New Experiment</button>}
			>
				{experiments.map((a) => (
					<div className="cop-pending-item" key={a.experiment_id}>
						<div className="cop-pending-text">
							<div className="t">
								{a.experiment_name}
								{a.source === 'custom' && <Pill tone="purple">custom</Pill>}
							</div>
							<div className="s">
								{a.experiment_type.replace(/_/g, ' ')}
								{a.target_segment_id ? ` · targets ${segmentName(a.target_segment_id) || a.target_segment_id}` : ''}
								{a.result ? ` · ${a.result}` : a.variants.length ? ` · ${a.variants.length} variants` : ''}
								{a.allocation_mode ? ` · ${ALLOCATION_MODES.find((m) => m.value === a.allocation_mode)?.label}` : ''}
							</div>
						</div>
						<StatusPill status={a.status} />
						{a.is_system_managed ? (
							<span style={{ fontSize: 11, color: 'var(--text-dim)' }}>read-only</span>
						) : (
							<div style={{ display: 'flex', gap: 6 }}>
								<button className="cop-btn small" onClick={() => openEdit(a)}>Edit</button>
								<button className="cop-btn small danger" onClick={() => setConfirmDelete(a)}>Delete</button>
							</div>
						)}
					</div>
				))}
			</Card>

			{winnerModal && (
				<Modal
					title="Declare Variant C the winner?"
					sub="This is a governed action. It creates a winner-declaration request in the Approval Queue — traffic does not shift until a human approves."
					onClose={() => setWinnerModal(false)}
					actions={
						<>
							<button className="cop-btn" onClick={() => setWinnerModal(false)}>Cancel</button>
							<button className="cop-btn primary" onClick={declareWinner}>Submit for Approval</button>
						</>
					}
				>
					<div className="cop-check-row"><span style={{ color: 'var(--green)' }}>✓</span> Best probability 89% for Variant C</div>
					<div className="cop-check-row"><span style={{ color: 'var(--green)' }}>✓</span> Guardrail metrics clean (AOV, bounce, unsubscribe)</div>
					{isInternal && (
						<div className="cop-check-row"><span style={{ color: 'var(--amber)' }}>!</span> 7 days remaining in planned run — extending narrows the interval</div>
					)}
				</Modal>
			)}

			{modal && (
				<Modal
					title={modal.mode === 'create' ? 'Create experiment' : 'Edit experiment'}
					sub="Configure variants and traffic split. Launching and declaring winners remain governed actions that route through the Approval Queue."
					onClose={() => (saving ? null : setModal(null))}
					actions={
						<>
							<button className="cop-btn" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
							<button className="cop-btn primary" onClick={submit} disabled={saving}>
								{saving ? 'Saving…' : modal.mode === 'create' ? 'Create experiment' : 'Save changes'}
							</button>
						</>
					}
				>
					<ExperimentForm form={form} setForm={setForm} segments={segments} error={error} />
				</Modal>
			)}

			{confirmDelete && (
				<Modal
					title={`Delete “${confirmDelete.experiment_name}”?`}
					sub="This removes the experiment. Imported (system-managed) experiments cannot be deleted."
					onClose={() => setConfirmDelete(null)}
					actions={
						<>
							<button className="cop-btn" onClick={() => setConfirmDelete(null)}>Cancel</button>
							<button className="cop-btn danger" onClick={doDelete}>Delete experiment</button>
						</>
					}
				>
					<div className="cop-check-row"><span style={{ color: 'var(--amber)' }}>!</span> Draft and concluded experiments can be removed; in-flight allocation history is not affected.</div>
				</Modal>
			)}
		</main>
	);
}
